import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowRight,
  LuAward,
  LuBox,
  LuCalendarClock,
  LuCheck,
  LuChevronDown,
  LuClipboardList,
  LuClock3,
  LuCloudUpload,
  LuEllipsis,
  LuHeadphones,
  LuInfo,
  LuMail,
  LuPackageX,
  LuRefreshCcw,
  LuScale,
  LuShieldCheck,
  LuShoppingCart,
  LuTag,
  LuTruck,
  LuUser,
  LuUserCog,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import PhoneCountryCodeSelect from "../../components/PhoneCountryCodeSelect";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS } from "../../constants/api-urls";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";
import { DISPUTE_CATEGORY_VALUES } from "../../services/myDispute.service";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/submit_dispute.scss";

const FALLBACK_CURRENCIES = [
  { value: "INR", label: "INR - Indian Rupee" },
  { value: "USD", label: "USD - US Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "AED", label: "AED - UAE Dirham" },
];

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"];

const DISPUTE_TYPES = [
  { id: "order", label: "Order Related", icon: LuShoppingCart, tone: "blue" },
  { id: "payment", label: "Payment Related", icon: LuShieldCheck, tone: "green" },
  { id: "supplier", label: "Supplier Related", icon: LuUserCog, tone: "orange" },
  { id: "buyer", label: "Buyer Related", icon: LuUser, tone: "purple" },
  { id: "logistics", label: "Logistics Related", icon: LuTruck, tone: "cyan" },
  { id: "service", label: "Service Related", icon: LuHeadphones, tone: "navy" },
  { id: "other", label: "Other", icon: LuEllipsis, tone: "gray" },
];

const DISPUTE_CATEGORY_OPTIONS = [
  { value: "Late Delivery", icon: LuClock3, tone: "orange" },
  { value: "Damaged Goods", icon: LuPackageX, tone: "coral" },
  { value: "Wrong Item", icon: LuTag, tone: "purple" },
  { value: "Quality Issue", icon: LuAward, tone: "green" },
  { value: "Quantity Mismatch", icon: LuBox, tone: "blue" },
  { value: "Payment Delay", icon: LuCalendarClock, tone: "cyan" },
  { value: "Refund Pending", icon: LuRefreshCcw, tone: "amber" },
  { value: "Service Issue", icon: LuHeadphones, tone: "purple" },
  { value: "Logistics Issue", icon: LuTruck, tone: "teal" },
  { value: "Other", icon: LuEllipsis, tone: "gray" },
].filter((item) => DISPUTE_CATEGORY_VALUES.includes(item.value));

const PROCESS_STEPS = [
  {
    title: "Submit Dispute",
    description: "Fill in the details and submit your dispute.",
    icon: LuClipboardList,
    tone: "blue",
  },
  {
    title: "Review Process",
    description: "Our team will review and may reach out for more information.",
    icon: LuCheck,
    tone: "green",
  },
  {
    title: "Resolution",
    description: "We will notify you about the decision via email and dashboard.",
    icon: LuScale,
    tone: "orange",
  },
];

function initialForm() {
  const user = StorageService.getUser() || {};
  return {
    disputeType: "order",
    referenceType: "Purchase Order (PO)",
    referenceNumber: "",
    orderNumber: "",
    orderDate: "",
    orderValue: "",
    currency: "",
    supplierName: "",
    supplierEmail: "",
    supplierContact: "",
    supplierCountryCode: "+91",
    supplierPhone: "",
    supplierAddress: "",
    category: "",
    priority: "",
    subject: "",
    description: "",
    contactName:
      user.name ||
      user.fullName ||
      user.full_name ||
      [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(" ") ||
      "",
    email: user.email || user.email_address || "",
    countryCode: "+91",
    phone: user.phone || user.mobile || user.mobile_number || "",
    declaration: false,
    attachment: null,
  };
}

function getUserDisplayName() {
  const user = StorageService.getUser() || {};
  return (
    user.name ||
    user.fullName ||
    user.full_name ||
    [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(" ") ||
    "Platform User"
  );
}

function getCompanyName() {
  const user = StorageService.getUser() || {};
  return user.company_name || user.companyName || user.company?.name || "";
}

export default function SubmitDisputePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const categoryMenuRef = useRef(null);
  const [form, setForm] = useState(initialForm);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const { currencyOptions, loading: currencyLoading } = useCurrencyMaster();

  const selectedCategory = useMemo(
    () => DISPUTE_CATEGORY_OPTIONS.find((item) => item.value === form.category) || null,
    [form.category],
  );
  const SelectedCategoryIcon = selectedCategory?.icon || null;

  useEffect(() => {
    if (!categoryOpen) return undefined;
    const onPointerDown = (event) => {
      if (!categoryMenuRef.current?.contains(event.target)) {
        setCategoryOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setCategoryOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [categoryOpen]);

  const currencySelectOptions = useMemo(() => {
    if (Array.isArray(currencyOptions) && currencyOptions.length > 0) {
      const seen = new Set();
      return currencyOptions
        .map((opt) => {
          const raw =
            (opt.currencyCode != null && String(opt.currencyCode).trim()) ||
            (typeof opt.value === "string" && String(opt.value).trim()) ||
            "";
          const code = /^[A-Za-z]{2,4}$/.test(raw) ? raw.toUpperCase() : raw;
          return {
            value: code,
            label: opt.label || code,
          };
        })
        .filter((opt) => opt.value && !seen.has(opt.value) && seen.add(opt.value));
    }
    return FALLBACK_CURRENCIES;
  }, [currencyOptions]);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const selectFile = (file) => {
    if (!file) return;
    const extension = `.${String(file.name || "").split(".").pop().toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error("Supported formats: JPG, PNG, PDF, DOC, DOCX, XLS and XLSX.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error("Attachment must be 10 MB or smaller.");
      return;
    }
    setForm((current) => ({ ...current, attachment: file }));
  };

  const validate = () => {
    if (!form.disputeType) return "Please select a dispute type.";
    if (!form.referenceNumber.trim()) return "Please enter the reference number.";
    if (!form.supplierName.trim()) return "Please enter the supplier name.";
    if (!form.category) return "Please select a dispute category.";
    if (!form.priority) return "Please select a priority.";
    if (!form.contactName.trim()) return "Please enter your name.";
    if (!form.subject.trim()) return "Please enter a subject.";
    if (form.subject.trim().length < 3) return "Subject must be at least 3 characters.";
    if (form.description.trim().length < 10) return "Description must be at least 10 characters.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return "Please enter a valid email address.";
    }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 8 || phoneDigits.length > 15) {
      return "Please enter a valid phone number.";
    }
    if (!form.declaration) return "Please accept the declaration.";
    return "";
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = new FormData();
    payload.append("fullName", form.contactName.trim() || getUserDisplayName());
    payload.append("companyName", getCompanyName());
    payload.append("email", form.email.trim());
    payload.append("countryCode", form.countryCode);
    payload.append("mobileNumber", form.phone.trim());
    payload.append("consentAccepted", "true");
    payload.append("disputeType", form.disputeType);
    payload.append("referenceType", form.referenceType);
    payload.append("referenceNumber", form.referenceNumber.trim());
    payload.append("orderNumber", form.orderNumber.trim());
    payload.append("orderDate", form.orderDate);
    payload.append("orderValue", form.orderValue);
    payload.append("currency", form.currency);
    payload.append("supplierName", form.supplierName.trim());
    payload.append("supplierEmail", form.supplierEmail.trim());
    payload.append("supplierContact", form.supplierContact.trim());
    payload.append("supplierCountryCode", form.supplierCountryCode);
    payload.append("supplierPhone", form.supplierPhone.trim());
    payload.append("supplierAddress", form.supplierAddress.trim());
    payload.append("category", form.category);
    payload.append("priority", form.priority);
    payload.append("subject", form.subject.trim());
    payload.append("description", form.description.trim());
    if (form.attachment) payload.append("attachment", form.attachment);

    setSubmitting(true);
    try {
      const response = await CommonService.post(API_URLS.DISPUTES.SUBMIT, payload, { silent: true });
      if (!response?.success) {
        throw new Error(response?.message || "Could not submit the dispute.");
      }
      const disputeNumber = response?.data?.disputeNumber;
      toast.success(
        disputeNumber
          ? `Your dispute has been submitted. Dispute ID: ${disputeNumber}`
          : "Your dispute has been submitted. Our support team will review it.",
      );
      setForm(initialForm());
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not submit the dispute. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="submit-dispute-page">
      <B2BHubHomeHeader />

      <main className="submit-dispute-main">
        <nav className="submit-dispute-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>&gt;</span>
          <Link to="/help-center">Help Center</Link>
          <span aria-hidden>&gt;</span>
          <span aria-current="page">Submit Dispute</span>
        </nav>

        <header className="submit-dispute-heading">
          <h1>Submit Dispute</h1>
          <p>Tell us what went wrong and we’ll review your dispute and get back to you.</p>
        </header>

        <div className="submit-dispute-layout">
          <form className="submit-dispute-form" onSubmit={onSubmit}>
            <fieldset className="submit-dispute-fieldset">
              <legend>
                1. Dispute Type <span>*</span>
              </legend>
              <div className="submit-dispute-types">
                {DISPUTE_TYPES.map((item) => {
                  const Icon = item.icon;
                  const selected = form.disputeType === item.id;
                  return (
                    <label
                      key={item.id}
                      className={`submit-dispute-type${selected ? " is-selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="disputeType"
                        value={item.id}
                        checked={selected}
                        onChange={onChange}
                      />
                      <span
                        className={`submit-dispute-type__icon submit-dispute-tone--${item.tone}`}
                        aria-hidden
                      >
                        <Icon />
                      </span>
                      <strong>{item.label}</strong>
                      <span className="submit-dispute-type__radio" aria-hidden />
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <section className="submit-dispute-section submit-dispute-section--reference">
              <h2>2. Reference Details</h2>
              <div className="submit-dispute-grid submit-dispute-grid--three">
                <label className="submit-dispute-field">
                  <span>Reference Type</span>
                  <select className="input-pill" name="referenceType" value={form.referenceType} onChange={onChange}>
                    <option>Purchase Order (PO)</option>
                    <option>Request for Quotation (RFQ)</option>
                    <option>Invoice</option>
                    <option>Contract</option>
                    <option>Shipment</option>
                  </select>
                </label>
                <label className="submit-dispute-field">
                  <span>
                    Reference Number <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="referenceNumber"
                    value={form.referenceNumber}
                    onChange={onChange}
                    placeholder="Enter reference number"
                    required
                  />
                </label>
                <label className="submit-dispute-field">
                  <span>Order Date</span>
                  <input className="input-pill" type="date" name="orderDate" value={form.orderDate} onChange={onChange} />
                </label>
                <label className="submit-dispute-field">
                  <span>Order Value (Optional)</span>
                  <input
                    className="input-pill"
                    type="number"
                    min="0"
                    name="orderValue"
                    value={form.orderValue}
                    onChange={onChange}
                    placeholder="Enter order value"
                  />
                </label>
                <label className="submit-dispute-field">
                  <span>Currency</span>
                  <select
                    className="input-pill"
                    name="currency"
                    value={form.currency}
                    onChange={onChange}
                    disabled={currencyLoading}
                  >
                    <option value="">
                      {currencyLoading ? "Loading currencies..." : "Select currency"}
                    </option>
                    {currencySelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="submit-dispute-field">
                  <span>Order Number (Optional)</span>
                  <input
                    className="input-pill"
                    name="orderNumber"
                    value={form.orderNumber}
                    onChange={onChange}
                    placeholder="Enter order number"
                  />
                </label>
              </div>
            </section>

            <section className="submit-dispute-section submit-dispute-section--supplier">
              <h2>3. Supplier Information</h2>
              <div className="submit-dispute-grid submit-dispute-grid--three">
                <label className="submit-dispute-field">
                  <span>
                    Supplier Name <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="supplierName"
                    value={form.supplierName}
                    onChange={onChange}
                    placeholder="Enter supplier name"
                    required
                  />
                </label>
                <label className="submit-dispute-field">
                  <span>Supplier Email</span>
                  <input
                    className="input-pill"
                    type="email"
                    name="supplierEmail"
                    value={form.supplierEmail}
                    onChange={onChange}
                    placeholder="Enter supplier email"
                  />
                </label>
                <label className="submit-dispute-field">
                  <span>Supplier Phone</span>
                  <span className="submit-dispute-phone">
                    <PhoneCountryCodeSelect
                      name="supplierCountryCode"
                      value={form.supplierCountryCode}
                      onChange={onChange}
                      aria-label="Supplier country code"
                    />
                    <input
                      className="input-pill"
                      type="tel"
                      name="supplierPhone"
                      value={form.supplierPhone}
                      onChange={onChange}
                      placeholder="Enter phone number"
                    />
                  </span>
                </label>
              </div>
            </section>

            <section className="submit-dispute-section submit-dispute-section--details">
              <h2>4. Dispute Details</h2>
              <div className="submit-dispute-details-grid">
                <div className="submit-dispute-field submit-dispute-field--category">
                  <span>
                    Category <em>*</em>
                  </span>
                  <div
                    className={`submit-dispute-category${categoryOpen ? " is-open" : ""}`}
                    ref={categoryMenuRef}
                  >
                    <button
                      type="button"
                      className="submit-dispute-category__trigger"
                      aria-haspopup="listbox"
                      aria-expanded={categoryOpen}
                      onClick={() => setCategoryOpen((open) => !open)}
                    >
                      {selectedCategory && SelectedCategoryIcon ? (
                        <span className="submit-dispute-category__value">
                          <span
                            className={`submit-dispute-category__icon submit-dispute-tone--${selectedCategory.tone}`}
                            aria-hidden
                          >
                            <SelectedCategoryIcon />
                          </span>
                          <span>{selectedCategory.value}</span>
                        </span>
                      ) : (
                        <span className="submit-dispute-category__placeholder">
                          Select category
                        </span>
                      )}
                      <LuChevronDown aria-hidden />
                    </button>
                    {categoryOpen ? (
                      <ul className="submit-dispute-category__menu" role="listbox">
                        {DISPUTE_CATEGORY_OPTIONS.map((item) => {
                          const Icon = item.icon;
                          const selected = form.category === item.value;
                          return (
                            <li key={item.value} role="option" aria-selected={selected}>
                              <button
                                type="button"
                                className={`submit-dispute-category__option${
                                  selected ? " is-selected" : ""
                                }`}
                                onClick={() => {
                                  setForm((current) => ({
                                    ...current,
                                    category: item.value,
                                  }));
                                  setCategoryOpen(false);
                                }}
                              >
                                <span
                                  className={`submit-dispute-category__icon submit-dispute-tone--${item.tone}`}
                                  aria-hidden
                                >
                                  <Icon />
                                </span>
                                <span>{item.value}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                  <input type="hidden" name="category" value={form.category} required />
                </div>
                <label className="submit-dispute-field submit-dispute-field--priority">
                  <span>
                    Priority <em>*</em>
                  </span>
                  <select className="input-pill" name="priority" value={form.priority} onChange={onChange} required>
                    <option value="">Select priority</option>
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Urgent</option>
                  </select>
                </label>
                <label className="submit-dispute-field submit-dispute-field--subject">
                  <span>
                    Subject <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    maxLength={160}
                    placeholder="Enter a short summary of the issue"
                    required
                  />
                </label>
                <label className="submit-dispute-field submit-dispute-field--description">
                  <span>
                    Description <em>*</em>
                  </span>
                  <span className="submit-dispute-textarea">
                    <textarea
                      className="input-area"
                      name="description"
                      value={form.description}
                      onChange={onChange}
                      maxLength={2000}
                      placeholder="Provide clear and detailed information about the issue you are facing"
                      required
                    />
                    <span>{form.description.length} / 2000</span>
                  </span>
                </label>
                <div className="submit-dispute-field submit-dispute-field--evidence">
                  <span className="submit-dispute-field__label">5. Supporting Evidence (Optional)</span>
                  <div
                    className={`submit-dispute-upload${dragging ? " is-dragging" : ""}`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDragging(false);
                      selectFile(event.dataTransfer.files?.[0]);
                    }}
                  >
                    <LuCloudUpload aria-hidden />
                    <div>
                      <strong>
                        {form.attachment ? form.attachment.name : "Drag and drop files here or"}
                      </strong>
                      <span>Supported: JPG, PNG, PDF, DOC, XLS (Max 10 MB)</span>
                    </div>
                    <button type="button" onClick={() => fileInputRef.current?.click()}>
                      Browse Files
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(event) => selectFile(event.target.files?.[0])}
                      hidden
                    />
                  </div>
                  <small>Upload images, documents or files that support your dispute.</small>
                </div>
              </div>
            </section>

            <div className="submit-dispute-field submit-dispute-section submit-dispute-section--contact">
              <span className="submit-dispute-field__label">6. Your Contact Information</span>
              <div className="submit-dispute-contact-grid">
                <label>
                  <span>
                    Your Name <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    type="text"
                    name="contactName"
                    value={form.contactName}
                    onChange={onChange}
                    placeholder="Enter your name"
                    required
                  />
                </label>
                <label>
                  <span>
                    Email <em>*</em>
                  </span>
                  <span className="submit-dispute-input-icon">
                    <input
                      className="input-pill"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="Enter your email address"
                      required
                    />
                    <LuMail aria-hidden />
                  </span>
                </label>
                <label>
                  <span>
                    Phone Number <em>*</em>
                  </span>
                  <span className="submit-dispute-phone">
                    <PhoneCountryCodeSelect
                      name="countryCode"
                      value={form.countryCode}
                      onChange={onChange}
                      aria-label="Country code"
                    />
                    <input
                      className="input-pill"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="Enter phone number"
                      required
                    />
                  </span>
                </label>
              </div>
            </div>

            <label className="submit-dispute-declaration">
              <span className="submit-dispute-declaration__check">
                <input
                  type="checkbox"
                  name="declaration"
                  checked={form.declaration}
                  onChange={onChange}
                />
                <span>
                  I confirm that the information provided is accurate and understand that our team
                  will review my dispute.
                </span>
              </span>
            </label>

            <div className="submit-dispute-actions">
              <button type="button" className="submit-dispute-btn submit-dispute-btn--cancel" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button
                type="submit"
                className="submit-dispute-btn submit-dispute-btn--submit"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit Dispute"}
                {!submitting ? <LuArrowRight aria-hidden /> : null}
              </button>
            </div>
          </form>

          <aside className="submit-dispute-aside">
            <section className="submit-dispute-side-card">
              <h2>How it works</h2>
              <div className="submit-dispute-process">
                {PROCESS_STEPS.map((step) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.title} className="submit-dispute-process__step">
                      <span
                        className={`submit-dispute-process__icon submit-dispute-tone--${step.tone}`}
                        aria-hidden
                      >
                        <Icon />
                      </span>
                      <div>
                        <strong>{step.title}</strong>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="submit-dispute-note">
              <h2>
                <LuInfo aria-hidden /> Important Note
              </h2>
              <p>
                Please provide clear and accurate information so we can resolve your issue faster.
              </p>
            </section>
          </aside>
        </div>
      </main>

      <HubHomeFooter />
    </div>
  );
}
